import { useEffect, useState } from 'react';
import { yearLabel } from '../lib/format';

type Props = {
  baseYear: number;
  onBaseYearChange: (year: number) => void;
  preparedDate: Date;
  source: 'seed' | 'saved';
  dirty: boolean;
  busy: boolean;
  savedNote: string;
};

// Hub-injected globals from Main.gs:renderPage_ (empty when running standalone on Cloudflare).
function readGlobal(key: string): string {
  try {
    const v = (window as unknown as Record<string, unknown>)[key];
    return typeof v === 'string' ? v : '';
  } catch {
    return '';
  }
}
function readGlobalBool(key: string): boolean {
  try {
    return Boolean((window as unknown as Record<string, unknown>)[key]);
  } catch {
    return false;
  }
}

const HUB_URL = readGlobal('__CCA_HUB_URL__');
const VISITOR_EMAIL = readGlobal('__CCA_VISITOR_EMAIL__');
const IS_OWNER = readGlobalBool('__CCA_IS_OWNER__');
const DISPLAY_NAME = readGlobal('__CCA_DISPLAY_NAME__');
const XERO_CONNECTED = readGlobalBool('__CCA_XERO_CONNECTED__');
const XERO_TENANT = readGlobal('__CCA_XERO_TENANT__');

function avatarInitials(email: string, displayName: string): string {
  if (displayName) {
    const parts = displayName
      .trim()
      .split(/\s+/)
      .filter((s) => s.length > 0);
    if (parts.length >= 2)
      return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
    if (parts.length === 1) return (parts[0]?.[0] ?? '?').toUpperCase();
  }
  if (!email) return '?';
  return (email[0] || '?').toUpperCase();
}

/**
 * Budget-tool metadata strip — year range, year picker, prepared date, save status.
 * Rendered by BudgetPage below the shared PortalHeader in the Finance Portal shell.
 * Also mounts the GAS account drawer when hub globals are present (standalone mode: no-op).
 */
export function Masthead({
  baseYear,
  onBaseYearChange,
  preparedDate,
  source,
  dirty,
  busy,
  savedNote,
}: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const initials = avatarInitials(VISITOR_EMAIL, DISPLAY_NAME);

  const projectionRange = `${yearLabel(baseYear, 1)} → ${yearLabel(baseYear, 2)} → ${yearLabel(baseYear, 3)}`;
  const formattedDate = preparedDate.toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <>
      {/* Budget metadata strip */}
      <div className="bg-white border-b border-edge no-print">
        <div className="max-w-[1100px] mx-auto px-7 py-4 flex items-center justify-between gap-6 flex-wrap">
          <div className="min-w-0">
            <div className="text-[13px] text-ccaanz-grey">
              Three-year forward projection · Current year {yearLabel(baseYear, 0)}
              <span className="px-1.5 text-ccaanz-gold">·</span>
              Projecting {projectionRange}
              <span className="px-1.5 text-ccaanz-gold">·</span>
              FY ends 30 September · NZD
            </div>
          </div>

          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-[0.08em] text-ccaanz-grey font-medium">
                Current Year
              </span>
              <input
                type="number"
                min={2020}
                max={2099}
                step={1}
                value={baseYear}
                onChange={(e) => {
                  const next = parseInt(e.target.value, 10);
                  if (!Number.isNaN(next)) onBaseYearChange(next);
                }}
                className="input num w-24 text-right text-[14px] font-medium"
              />
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-[0.08em] text-ccaanz-grey font-medium">
                Prepared
              </span>
              <span className="text-[13px] text-ccaanz-black font-medium">{formattedDate}</span>
            </div>

            <SaveStatus source={source} dirty={dirty} busy={busy} savedNote={savedNote} />

            {/* GAS hub user avatar — only shows when hub globals injected */}
            {VISITOR_EMAIL && (
              <button
                type="button"
                aria-label="Account"
                aria-haspopup="dialog"
                onClick={() => setDrawerOpen(true)}
                className="w-10 h-10 rounded-full bg-ccaanz-gold hover:bg-ccaanz-gold-dark text-white text-[14px] font-semibold flex items-center justify-center flex-shrink-0 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ccaanz-blue focus-visible:ring-offset-2"
              >
                {initials}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* GAS account drawer — no-op when standalone */}
      <AccountDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        initials={initials}
        email={VISITOR_EMAIL}
        displayName={DISPLAY_NAME}
        isOwner={IS_OWNER}
        xeroConnected={XERO_CONNECTED}
        xeroTenant={XERO_TENANT}
        hubUrl={HUB_URL}
      />
    </>
  );
}

function AccountDrawer({
  open,
  onClose,
  initials,
  email,
  displayName,
  isOwner,
  xeroConnected,
  xeroTenant,
  hubUrl,
}: {
  open: boolean;
  onClose: () => void;
  initials: string;
  email: string;
  displayName: string;
  isOwner: boolean;
  xeroConnected: boolean;
  xeroTenant: string;
  hubUrl: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!email) return null;

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- backdrop click-to-close duplicates the drawer's Escape-key handler and explicit close control */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-[998] bg-ccaanz-black/30 transition-opacity ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
      />
      <aside
        role="dialog"
        aria-label="Account"
        aria-hidden={!open}
        className={`fixed top-0 right-0 bottom-0 w-[320px] max-w-[88vw] bg-paper-cream border-l border-edge shadow-[-8px_0_24px_rgba(0,0,0,0.06)] z-[999] flex flex-col transition-transform duration-200 ease-out ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="px-[22px] py-4 border-b border-paper-divider flex items-center justify-between">
          <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-ccaanz-grey">
            Account
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="bg-transparent border-0 text-ccaanz-grey hover:text-ccaanz-black hover:bg-ccaanz-gold-light text-[22px] leading-none px-2 py-0.5 rounded-md cursor-pointer"
          >
            ×
          </button>
        </div>
        <div className="px-[22px] py-[22px] flex-1 overflow-y-auto">
          <div className="w-16 h-16 rounded-full bg-ccaanz-gold text-white text-[22px] font-semibold flex items-center justify-center mb-[14px]">
            {initials}
          </div>
          {displayName && (
            <div className="text-[18px] font-semibold text-ccaanz-black leading-tight mb-0.5 break-words">
              {displayName}
            </div>
          )}
          <div className="text-[13px] text-ccaanz-grey break-words">{email}</div>
          <div className="h-px bg-paper-divider my-[18px]" />
          <DrawerField label="Role" value={isOwner ? 'Owner' : 'Authorised user'} />
          <DrawerField
            label="Xero"
            value={
              <>
                {xeroConnected ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-pill bg-ccaanz-green-light text-ccaanz-green text-[12px] font-medium">
                    <span className="w-[7px] h-[7px] rounded-full bg-current opacity-85" />{' '}
                    Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-pill bg-ccaanz-gold-light text-ccaanz-grey text-[12px] font-medium">
                    <span className="w-[7px] h-[7px] rounded-full bg-current opacity-85" /> Not
                    connected
                  </span>
                )}
                {xeroConnected && xeroTenant && (
                  <div className="mt-1.5 text-[13px] text-ccaanz-grey">{xeroTenant}</div>
                )}
                {hubUrl && isOwner && (
                  <a
                    href={`${hubUrl}?xero=connect`}
                    className={`mt-2 inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-control text-[13px] font-medium no-underline transition-colors ${xeroConnected ? 'bg-transparent text-ccaanz-blue border border-edge hover:bg-ccaanz-gold-light hover:text-ccaanz-blue-dark' : 'bg-ccaanz-blue text-white hover:bg-ccaanz-blue-dark'}`}
                  >
                    {xeroConnected ? 'Reconnect' : 'Connect Xero'}
                  </a>
                )}
                {!xeroConnected && !isOwner && (
                  <div className="mt-1.5 text-[12px] text-ccaanz-grey">
                    Ask the registrar to authorise the connection.
                  </div>
                )}
              </>
            }
          />
          {isOwner && hubUrl && (
            <DrawerField
              label="Admin"
              value={
                <a
                  href={`${hubUrl}?page=admin`}
                  className="text-ccaanz-blue no-underline hover:underline"
                >
                  Manage authorised users
                </a>
              }
            />
          )}
          <DrawerField
            label="Help"
            value={
              <a
                href="mailto:registrar@confessinganglicans.nz"
                className="text-ccaanz-blue no-underline hover:underline"
              >
                registrar@confessinganglicans.nz
              </a>
            }
          />
        </div>
        <div className="px-[22px] py-4 border-t border-paper-divider text-[11px] text-ccaanz-grey leading-normal">
          Sign-in is tied to your Google account. To switch accounts, use Google's account menu in
          your browser.
        </div>
      </aside>
    </>
  );
}

function DrawerField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ccaanz-grey mt-3.5 mb-1 first:mt-0">
        {label}
      </div>
      <div className="text-[14px] text-ccaanz-black break-words">{value}</div>
    </>
  );
}

function SaveStatus({
  source,
  dirty,
  busy,
  savedNote,
}: {
  source: 'seed' | 'saved';
  dirty: boolean;
  busy: boolean;
  savedNote: string;
}) {
  if (busy) return <span className="pill pill-warn">Working…</span>;
  if (savedNote) return <span className="pill pill-info">● {savedNote}</span>;
  if (dirty) return <span className="pill pill-warn">● Unsaved changes</span>;
  return (
    <span className="pill pill-neutral">
      ● {source === 'saved' ? 'All changes saved' : 'Loaded from seed'}
    </span>
  );
}
